import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useLocation, useParams } from 'react-router-dom';
import { useTenantNav } from '../hooks/useTenantNav';
import { portfoliosApi, PortfolioMetricsResponse } from '../api/ppm';
import { usersApi, DbUser } from '../api/client';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canEditPage } from '../utils/rbac';
import { PageHeader } from './layout/PageHeader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut } from './charts/NexusCharts';
import { tokens, chartColor } from '../theme/tokens';
import { computeFinance, formatCurrencyShort, formatSignedCurrencyShort } from '../utils/finance';
import { ResponsiveContainer, Tooltip as ReTooltip, Sankey } from 'recharts';

const statusColor = (status: string) => {
  const s = (status || '').toUpperCase();
  if (s.includes('ACTIVE')) return 'success';
  if (s.includes('PLAN')) return 'info';
  if (s.includes('HOLD')) return 'warning';
  if (s.includes('COMP')) return 'default';
  if (s.includes('ARCH')) return 'default';
  return 'default';
};

const PortfolioDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { navigate } = useTenantNav();
  const location = useLocation();
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'portfolios');

  const [tab, setTab] = useState<'overview' | 'projects' | 'members'>('overview');
  const [data, setData] = useState<PortfolioMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<DbUser[]>([]);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [memberForm, setMemberForm] = useState({ userId: '', role: 'member' });
  const [memberSaving, setMemberSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const res = await portfoliosApi.getMetrics(id);
        if (!mounted) return;
        setData(res.data);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load portfolio');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await usersApi.getAll({ scopePage: 'portfolios' });
        if (!mounted) return;
        setAllUsers((res.data || []) as DbUser[]);
      } catch {
        if (!mounted) return;
        setAllUsers([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const portfolio = data?.portfolio;
  const metrics = data?.metrics;

  const projects = useMemo(() => portfolio?.Projects || [], [portfolio]);
  const members = useMemo(() => {
    const list: Array<{ id: string; role?: string | null; User?: any; userId?: string; source: 'explicit' | 'derived' }> = [];

    const explicit = (portfolio as any)?.PortfolioMembers || [];
    (explicit || []).forEach((m: any) => {
      const uid = String(m?.User?.id || m?.userId || '');
      list.push({
        id: String(m?.id || `pm_${uid}`),
        role: m?.role || 'member',
        User: m?.User || null,
        userId: uid || undefined,
        source: 'explicit',
      });
    });

    // Treat portfolio owner + project lead/manager as members so the tab isn't empty unless the portfolio truly has no people attached.
    const owner = (portfolio as any)?.Owner || null;
    if (owner?.id) {
      list.push({ id: `derived_owner_${String(owner.id)}`, role: 'owner', User: owner, userId: String(owner.id), source: 'derived' });
    }

    (projects || []).forEach((p: any) => {
      const lead = p?.Lead || null;
      const manager = p?.Manager || null;
      if (lead?.id) list.push({ id: `derived_lead_${String(lead.id)}_${String(p?.id || '')}`, role: 'project lead', User: lead, userId: String(lead.id), source: 'derived' });
      if (manager?.id) list.push({ id: `derived_mgr_${String(manager.id)}_${String(p?.id || '')}`, role: 'project manager', User: manager, userId: String(manager.id), source: 'derived' });
    });

    // Deduplicate by user id, preferring explicit PortfolioMember role (if present)
    const byUser = new Map<string, { id: string; role?: string | null; User?: any; _priority: number }>();
    list.forEach((m) => {
      const uid = String(m?.userId || m?.User?.id || '');
      if (!uid) return;
      const priority = String(m.id || '').startsWith('pm_') || !String(m.id || '').startsWith('derived_') ? 2 : 1;
      const existing = byUser.get(uid);
      if (!existing || priority > existing._priority) {
        byUser.set(uid, { ...m, _priority: priority });
      }
    });

    return Array.from(byUser.values())
      .map(({ _priority, ...rest }) => rest)
      .sort((a, b) => String(a?.User?.displayName || a?.User?.email || '').localeCompare(String(b?.User?.displayName || b?.User?.email || '')));
  }, [portfolio, projects]);

  const moneyCompact = useMemo(() => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
  }, []);
  const moneyFull = useMemo(() => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  }, []);

  const budgetBars = useMemo(() => {
    const totalBudget = Number(metrics?.totalBudget || 0);
    const totalSpent = Number(metrics?.totalSpent || 0);
    return [
      { name: 'Budget', value: totalBudget },
      { name: 'Allocated', value: totalSpent },
      { name: 'Remaining', value: Math.max(0, totalBudget - totalSpent) },
    ];
  }, [metrics?.totalBudget, metrics?.totalSpent]);

  const finance = useMemo(
    () =>
      computeFinance({
        budget: Number(metrics?.totalBudget || 0),
        allocated: Number(metrics?.totalSpent || 0),
        spent: Number(metrics?.totalSpent || 0),
      }),
    [metrics?.totalBudget, metrics?.totalSpent],
  );

  const projectStatusMix = useMemo(() => {
    const map = new Map<string, number>();
    (projects || []).forEach((p: any) => {
      const s = String(p.status || 'UNKNOWN').toUpperCase();
      map.set(s, (map.get(s) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [projects]);

  const fundingDistribution = useMemo(() => {
    const totalBudget = Number(metrics?.totalBudget || 0);
    // NOTE: metrics.totalSpent is "allocated" (budgeted cost rollup). For this Sankey we show Budget vs Cost,
    // so "actual" should come from project.actualCost (kept in sync by Financials entries).
    // Include PLANNING so newly created projects show up immediately in the distribution.
    const activeStatuses = new Set(['PLANNING', 'ACTIVE', 'ON_TRACK', 'AT_RISK', 'OFF_TRACK']);

    const active = (projects || [])
      .filter((p: any) => activeStatuses.has(String(p.status || '').toUpperCase()))
      .map((p: any) => ({
        projectId: String(p.id),
        jiraKey: p.jiraKey ? String(p.jiraKey) : null,
        name: String(p.name || 'Untitled project'),
        budget: Math.max(0, Number(p.budgetedCost || 0)),
        actual: Math.max(0, Number(p.actualCost || 0)),
      }))
      .filter((p) => p.budget > 0)
      .sort((a, b) => b.budget - a.budget);

    const MAX_NODES = 7;
    const shown = active.slice(0, MAX_NODES);
    const shownSum = shown.reduce((s, x) => s + x.budget, 0);
    const shownActualSum = shown.reduce((s, x) => s + x.actual, 0);
    const activeActualTotal = active.reduce((s, x) => s + x.actual, 0);
    const otherValue = Math.max(0, totalBudget - shownSum);
    const otherActual = Math.max(0, activeActualTotal - shownActualSum);

    const nodes = [
      { name: portfolio?.name || 'Portfolio', projectId: null as any, jiraKey: null as any, budget: totalBudget, actual: activeActualTotal },
      ...shown.map((p) => ({ name: p.name, projectId: p.projectId, jiraKey: p.jiraKey, budget: p.budget, actual: p.actual })),
      { name: 'Other', projectId: null as any, jiraKey: null as any, budget: otherValue, actual: otherActual },
    ];
    const links = [
      ...shown.map((p, i) => ({ source: 0, target: i + 1, value: p.budget, actual: p.actual, projectId: p.projectId, jiraKey: p.jiraKey, label: p.name })),
      { source: 0, target: nodes.length - 1, value: otherValue, actual: otherActual, projectId: null, jiraKey: null, label: 'Other' },
    ].filter((l) => Number(l.value || 0) > 0);

    return { nodes, links };
  }, [metrics?.totalBudget, portfolio?.name, projects]);

  const openProject = (projectId: string, jiraKey?: string | null) => {
    navigate(jiraKey ? `/projects/${jiraKey}` : `/projects/internal/${projectId}`, { state: { from: location.pathname } });
  };

  const SankeyTooltip: React.FC<any> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const first = payload?.[0] || {};
    const p = first?.payload || {};
    const name = p?.name || p?.label || first?.name || first?.label || '—';
    const budgetRaw = (() => {
      if (typeof first?.value === 'number') return first.value;
      if (typeof first?.value === 'string' && first.value.trim() !== '') return Number(first.value);
      if (p && typeof p.value === 'number') return p.value;
      if (p && typeof p.value === 'string' && String(p.value).trim() !== '') return Number(p.value);
      const deep = p?.payload;
      if (deep && typeof deep.value === 'number') return deep.value;
      if (deep && typeof deep.value === 'string' && String(deep.value).trim() !== '') return Number(deep.value);
      return 0;
    })();

    const actualRaw = (() => {
      const direct = p && (p.actual ?? p.actualCost ?? p.spent);
      if (typeof direct === 'number') return direct;
      if (typeof direct === 'string' && direct.trim() !== '') return Number(direct);
      const deep = p?.payload;
      const d2 = deep && (deep.actual ?? deep.actualCost ?? deep.spent);
      if (typeof d2 === 'number') return d2;
      if (typeof d2 === 'string' && String(d2).trim() !== '') return Number(d2);
      return 0;
    })();

    const budget = moneyFull.format(Number(budgetRaw || 0));
    const actual = moneyFull.format(Number(actualRaw || 0));
    return (
      <div
        style={{
          background: tokens.color.surface,
          color: tokens.color.text,
          padding: '10px 12px',
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.color.border}`,
          boxShadow: tokens.shadow.pop,
          maxWidth: 320,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {String(name)}
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, color: tokens.color.textSecondary }}>Budget: {budget}</div>
        <div style={{ fontWeight: 600, fontSize: 13, color: tokens.color.textSecondary }}>Cost: {actual}</div>
      </div>
    );
  };

  const SankeyNode: React.FC<any> = (props) => {
    const { x, y, width, height, index, payload } = props || {};

    const fill =
      index === 0
        ? tokens.color.accent
        : payload?.name === 'Other'
          ? tokens.color.neutral
          : chartColor(Number(index || 1) - 1);

    const label = payload?.name ? String(payload.name) : '';
    const showLabel = index === 0; // portfolio label lives inside the left node

    return (
      <g>
        <rect x={x} y={y} width={width} height={height} fill={fill} rx={2} />
        {showLabel ? (
          <text
            x={Number(x || 0) + Number(width || 0) + 30}
            y={Number(y || 0) + Number(height || 0) / 2}
            textAnchor="start"
            dominantBaseline="middle"
            fontSize={16}
            fill={tokens.color.text}
            fontWeight={700}
          >
            {label}
          </text>
        ) : null}
      </g>
    );
  };

  const SankeyLink: React.FC<any> = (props) => {
    const { sourceX, sourceY, targetX, targetY, linkWidth, payload, index } = props || {};
    const label = payload?.label ? String(payload.label) : '';
    const projectId = payload?.projectId ? String(payload.projectId) : null;
    const jiraKey = payload?.jiraKey ? String(payload.jiraKey) : null;

    const isClickable = Boolean(projectId);

    const sx = Number(sourceX || 0);
    const sy = Number(sourceY || 0);
    const tx = Number(targetX || 0);
    const ty = Number(targetY || 0);
    const w = Math.max(1, Number(linkWidth || 1));
    const curvature = 0.5;
    const ix = sx + (tx - sx) * curvature;
    const ix2 = tx - (tx - sx) * curvature;

    const d = `M${sx},${sy} C${ix},${sy} ${ix2},${ty} ${tx},${ty}`;
    const pathId = `sankey-link-${String(index ?? '')}`;

    const text = label.length > 30 ? `${label.slice(0, 28)}…` : label;

    return (
      <g
        onClick={() => {
          if (projectId) openProject(projectId, jiraKey);
        }}
        style={{ cursor: isClickable ? 'pointer' : 'default' }}
      >
        <defs>
          <path id={pathId} d={d} />
        </defs>
        <path d={d} fill="none" stroke={tokens.color.accent} strokeOpacity={0.35} strokeWidth={w} />
        {label ? (
          <text fontSize={11} fill={tokens.color.text} fontWeight={600} pointerEvents="none">
            <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle" dominantBaseline="middle">
              {text}
            </textPath>
          </text>
        ) : null}
      </g>
    );
  };

  const projectsCols = useMemo(() => {
    const cols = [
      {
        field: 'name',
        headerName: 'Project',
        flex: 1,
        minWidth: 260,
        renderCell: (params: any) => (
          <Button
            variant="text"
            onClick={() => {
              const row = params.row as any;
              const jiraKey = row?.jiraKey;
              navigate(jiraKey ? `/projects/${jiraKey}` : `/projects/internal/${row.id}`, { state: { from: location.pathname } });
            }}
            sx={{ textTransform: 'none', fontWeight: 650 }}
          >
            {params.value || 'Untitled project'}
          </Button>
        ),
      },
      {
        field: 'source',
        headerName: 'Source',
        width: 110,
        valueGetter: (p: any) => (p.row?.jiraKey ? 'Jira' : 'Internal'),
      },
      {
        field: 'jiraKey',
        headerName: 'Key',
        width: 120,
        valueGetter: (p: any) => (p.row?.jiraKey ? String(p.row.jiraKey) : '—'),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        renderCell: (params: any) => (
          <Chip size="small" variant="outlined" label={params.value ? String(params.value) : '—'} sx={{ height: 22, fontWeight: 650 }} />
        ),
      },
      {
        field: 'percentComplete',
        headerName: '% Complete',
        width: 130,
        valueGetter: (p: any) => {
          const v = Number(p.row?.percentComplete);
          return Number.isFinite(v) ? Math.round(v) : null;
        },
        renderCell: (p: any) => (p.value == null ? '—' : <span style={{ fontWeight: 700 }}>{p.value}%</span>),
      },
      {
        field: 'allocatedBudget',
        headerName: 'Allocated',
        width: 140,
        valueGetter: (p: any) => Number(p.row?.budgetedCost || 0),
        renderCell: (p: any) => <span style={{ fontWeight: 700 }}>{moneyCompact.format(Number(p.value || 0))}</span>,
      },
      {
        field: 'actualCost',
        headerName: 'Spend',
        width: 140,
        valueGetter: (p: any) => Number(p.row?.actualCost || 0),
        renderCell: (p: any) => <span style={{ fontWeight: 700 }}>{moneyCompact.format(Number(p.value || 0))}</span>,
      },
      {
        field: 'remaining',
        headerName: 'Remaining',
        width: 140,
        valueGetter: (p: any) => Number(p.row?.budgetedCost || 0) - Number(p.row?.actualCost || 0),
        renderCell: (p: any) => <span style={{ fontWeight: 700 }}>{moneyCompact.format(Number(p.value || 0))}</span>,
      },
      {
        field: 'utilization',
        headerName: 'Util %',
        width: 110,
        valueGetter: (p: any) => {
          const b = Number(p.row?.budgetedCost || 0);
          const a = Number(p.row?.actualCost || 0);
          if (!b || b <= 0) return null;
          return Math.round((a / b) * 100);
        },
        renderCell: (p: any) => (p.value == null ? '—' : <span style={{ fontWeight: 700 }}>{p.value}%</span>),
      },
      {
        field: 'lead',
        headerName: 'Lead',
        width: 200,
        valueGetter: (p: any) => p.row?.Lead?.displayName || p.row?.Lead?.email || '—',
      },
      {
        field: 'manager',
        headerName: 'Manager',
        width: 200,
        valueGetter: (p: any) => p.row?.Manager?.displayName || p.row?.Manager?.email || '—',
      },
      {
        field: 'dates',
        headerName: 'Dates',
        width: 200,
        valueGetter: (p: any) => {
          const s = p.row?.startDate ? String(p.row.startDate).slice(0, 10) : '';
          const e = p.row?.plannedEndDate ? String(p.row.plannedEndDate).slice(0, 10) : '';
          if (!s && !e) return '—';
          return `${s || '—'} → ${e || '—'}`;
        },
      },
    ];
    return cols;
  }, [location.pathname, moneyCompact, navigate]);

  const membersCols = useMemo(() => {
    const cols: any[] = [
      {
        field: 'user',
        headerName: 'User',
        flex: 1,
        minWidth: 240,
        valueGetter: (p: any) => p.row?.User?.displayName || p.row?.User?.email || '—',
      },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 260, valueGetter: (p: any) => p.row?.User?.email || '—' },
      { field: 'role', headerName: 'Role', width: 180, valueGetter: (p: any) => p.row?.role || '—' },
      { field: 'source', headerName: 'Source', width: 120, valueGetter: (p: any) => (p.row?.source === 'explicit' ? 'Portfolio' : 'Derived') },
    ];

    if (canEdit) {
      cols.push({
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (p: any) => {
          const row = p.row as any;
          const canRemove = row?.source === 'explicit' && id && row?.userId;
          return (
            <Button
              size="small"
              color="error"
              variant="text"
              disabled={!canRemove || memberSaving}
              onClick={async () => {
                if (!id || !row?.userId) return;
                try {
                  setMemberSaving(true);
                  await portfoliosApi.removeMember(id, String(row.userId));
                  const res = await portfoliosApi.getMetrics(id);
                  setData(res.data);
                } catch (e: any) {
                  setError(e?.response?.data?.message || e?.message || 'Failed to remove member');
                } finally {
                  setMemberSaving(false);
                }
              }}
              sx={{ fontWeight: 700 }}
            >
              Remove
            </Button>
          );
        },
      });
    }

    return cols;
  }, [id, canEdit, memberSaving]);

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title={portfolio?.name || 'Portfolio'}
        subtitle="Portfolio overview, budgets, projects, and members."
        actions={
          <>
            <Chip size="small" label={portfolio?.status || '—'} color={statusColor(portfolio?.status || '') as any} variant="outlined" sx={{ fontWeight: 700 }} />
            {Array.isArray((portfolio as any)?.strategicAlignments) && (portfolio as any).strategicAlignments.length ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Alignment: ${(portfolio as any).strategicAlignments.join(', ')}`}
                sx={{ fontWeight: 700 }}
              />
            ) : portfolio?.strategicAlignment ? (
              <Chip size="small" variant="outlined" label={`Alignment: ${portfolio.strategicAlignment}`} sx={{ fontWeight: 700 }} />
            ) : null}
            {portfolio?.Owner?.email ? <Chip size="small" variant="outlined" label={`Owner: ${portfolio.Owner.email}`} sx={{ fontWeight: 700 }} /> : null}
            <Button variant="outlined" onClick={() => navigate('/portfolios')} sx={{ borderRadius: 2 }}>
              Back
            </Button>
          </>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <TableCard
        title="Portfolio details"
        subtitle={tab === 'overview' ? 'Key metrics and visuals.' : tab === 'projects' ? 'Projects linked to this portfolio.' : 'Members assigned to this portfolio.'}
        actions={
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab value="overview" label="Overview" />
            <Tab value="projects" label={`Projects (${projects.length})`} />
            <Tab value="members" label={`Members (${members.length})`} />
          </Tabs>
        }
      >
        {tab === 'overview' && metrics ? (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}>
                <StatCard
                  title="Total Projects"
                  value={metrics.totalProjects}
                  subtitle="All projects"
                  accent={tokens.color.accent}
                  onClick={() => setTab('projects')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard
                  title="Active Projects"
                  value={metrics.activeProjects}
                  subtitle="In progress"
                  accent={tokens.color.success}
                  onClick={() => setTab('projects')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard
                  title="Budget Utilization"
                  value={`${Math.round(Number(metrics.budgetUtilization || 0))}%`}
                  subtitle={`${moneyFull.format(Number(metrics.totalSpent || 0))} / ${moneyFull.format(Number(metrics.totalBudget || 0))}`}
                  accent={tokens.color.accent}
                  onClick={() => setTab('projects')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard
                  title="Variance"
                  value={formatSignedCurrencyShort(finance.variance)}
                  subtitle={`${finance.varianceLabel} vs budget`}
                  tone={finance.tone}
                  accent={
                    finance.tone === 'favorable'
                      ? tokens.color.success
                      : finance.tone === 'unfavorable'
                        ? tokens.color.danger
                        : tokens.color.warning
                  }
                  onClick={() => setTab('projects')}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <ChartCard title="Budget vs allocated" subtitle="Portfolio rollup" height={260}>
                  <NexusBarChart
                    data={budgetBars}
                    xKey="name"
                    series={[{ key: 'value', name: 'Amount', color: tokens.color.accent }]}
                    valueFormatter={(v) => formatCurrencyShort(v)}
                    height={232}
                  />
                </ChartCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <ChartCard title="Projects by status" subtitle="Distribution" height={260}>
                  <NexusDonut
                    data={projectStatusMix.map((d, i) => ({ name: d.name, value: d.value, color: chartColor(i) }))}
                    valueFormatter={(v) => String(v)}
                    height={200}
                    centerLabel="Projects"
                    centerValue={String(projects.length)}
                  />
                </ChartCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <ChartCard title="Risk / issue counts" subtitle="Operational snapshot" height={260}>
                  <NexusBarChart
                    data={[
                      { name: 'Risks', value: Number(metrics.totalRisks || 0) },
                      { name: 'Issues', value: Number(metrics.totalIssues || 0) },
                    ]}
                    xKey="name"
                    series={[{ key: 'value', name: 'Count', color: tokens.color.accent }]}
                    valueFormatter={(v) => String(v)}
                    height={232}
                  />
                </ChartCard>
              </Grid>

              <Grid item xs={12}>
                <ChartCard title="Funding Distribution" subtitle="Active allocations vs. other" height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <Sankey
                      data={fundingDistribution as any}
                      nodeWidth={14}
                      nodePadding={18}
                      linkCurvature={0.5}
                      node={<SankeyNode />}
                      link={<SankeyLink />}
                      margin={{ top: 10, right: 18, left: 10, bottom: 10 }}
                    >
                      <ReTooltip content={<SankeyTooltip />} />
                    </Sankey>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>
          </>
        ) : null}

        {tab === 'projects' ? (
          <div style={{ width: '100%', height: 620 }}>
            <ResizableDataGrid
              storageKey={`gridWidths.portfolioDetail.projects.${String(id || '')}`}
              enableDensityControls
              rows={projects as any[]}
              columns={projectsCols as any}
              getRowId={(r) => (r as any).id}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': { backgroundColor: tokens.color.surfaceMuted },
                '& .MuiDataGrid-row:hover': { backgroundColor: tokens.color.surfaceMuted },
              }}
            />
          </div>
        ) : null}

        {tab === 'members' ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setMemberForm({ userId: '', role: 'member' });
                  setMemberDialogOpen(true);
                }}
                sx={{ borderRadius: 2 }}
                disabled={!canEdit}
              >
                Add member
              </Button>
            </Box>
            <div style={{ width: '100%', height: 620 }}>
              <ResizableDataGrid
                storageKey={`gridWidths.portfolioDetail.members.${String(id || '')}`}
                enableDensityControls
                rows={members as any[]}
                columns={membersCols as any}
                getRowId={(r) => (r as any).id}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: 'rgba(17,24,39,0.02)' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(17,24,39,0.03)' },
                }}
              />
            </div>
          </>
        ) : null}
      </TableCard>

      <Dialog open={memberDialogOpen} onClose={() => setMemberDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Add portfolio member</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              select
              label="User"
              value={memberForm.userId}
              onChange={(e) => setMemberForm((v) => ({ ...v, userId: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">
                <em>Select a user…</em>
              </MenuItem>
              {allUsers.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.displayName || u.email}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Role"
              value={memberForm.role}
              onChange={(e) => setMemberForm((v) => ({ ...v, role: e.target.value }))}
              fullWidth
              helperText="Examples: member, lead, viewer"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setMemberDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!id || !memberForm.userId || memberSaving}
            onClick={async () => {
              if (!id || !memberForm.userId) return;
              try {
                setMemberSaving(true);
                await portfoliosApi.addMember(id, { userId: memberForm.userId, role: memberForm.role || 'member' });
                const res = await portfoliosApi.getMetrics(id);
                setData(res.data);
                setMemberDialogOpen(false);
              } catch (e: any) {
                setError(e?.response?.data?.message || e?.message || 'Failed to add member');
              } finally {
                setMemberSaving(false);
              }
            }}
            sx={{ borderRadius: 2 }}
          >
            {memberSaving ? 'Saving…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PortfolioDetail;


